import { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, addGroupAlias } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { ApiException, DirectoryGroup } from '../../../../../types';
import axios from 'axios';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import SharedDialog from '../../../../shared/shared-dialog';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
interface AddGroupAliasDialogProps {
  open: boolean;
  handleClose: () => void;
  domains: string[];
  group: DirectoryGroup;
  changeLogRoles: string[];
  primaryDomain: string;
}

export default function AddGroupAliasDialog({
  open,
  handleClose,
  domains,
  group,
  changeLogRoles,
  primaryDomain,
}: AddGroupAliasDialogProps) {
  const { createEvent } = useAnalytics();
  const [groupName, setGroupName] = useState('');
  const [hasError, setHasError] = useState(false);
  const [domain, setDomain] = useState(primaryDomain);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      addGroupAlias(group.email, `${groupName}@${domain}`, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Group alias created successfully!',
      });

      const currentAliases = group.aliases || [];

      queryClient.setQueryData([UserKeys.DirectoryGroup, group.email], {
        ...group,
        aliases: [`${groupName}@${domain}`, ...currentAliases],
      });

      setGroupName('');
      setDomain(primaryDomain);
      setHasError(false);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message?.includes('409')) {
          setHasError(true);
        } else {
          handleErrors({
            error,
            snackbarMessage:
              'There was an issue creating the group alias. Please refresh and try again.',
          });
        }
      }
    },
    onSettled: () => {
      createEvent(analytics.directory.group_details.create_group_alias, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, DirectoryRoles.AddGroupAliases.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={() => {
          setGroupName('');
          setHasError(false);
          setDomain(primaryDomain);
          handleClose();
        }}
        title="Add Alias"
        dialogContent={
          <Stack gap={2}>
            <Typography>
              Add alias for <strong>{group.name}</strong>.
            </Typography>
            <Stack flexDirection="row" alignItems="baseline" gap={1}>
              <TextField
                id="alias"
                error={hasError}
                helperText={hasError ? 'Alias already exists.' : ' '}
                label="Enter alias"
                fullWidth
                autoComplete="off"
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
              />
              <Typography>@</Typography>
              <TextField
                select
                error={hasError}
                fullWidth
                label="Domain"
                value={domain}
                onChange={(event) => setDomain(event.target.value)}
              >
                {domains.map((domain) => (
                  <MenuItem value={domain} key={domain}>
                    {domain}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>
        }
        primaryButton={{
          loading: isPending,
          action: handleSubmit,
          disabled: groupName === '',
          title: 'Add Alias',
        }}
      />
      {openChangeLogDialog && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={mutate}
          mutateLoading={isPending}
        />
      )}
    </>
  );
}
